import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Edit3, Sparkles, Zap } from 'lucide-react';

function FacetBox({ facet, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(facet.value);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(facet.value);
  }, [facet.value]);

  useEffect(() => {
    if (!editing) return;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (next) onChange(facet.key, next);
    setEditing(false);
  };

  return (
    <div
      className={`group rounded-xl border p-3.5 transition-colors ${
        editing
          ? 'border-brand-400 bg-white ring-2 ring-brand-100 dark:border-brand-600 dark:bg-gray-900 dark:ring-brand-900/30'
          : 'border-gray-200 bg-gray-50/80 hover:border-brand-200 hover:bg-white dark:border-gray-700 dark:bg-gray-800/50 dark:hover:border-brand-800 dark:hover:bg-gray-800'
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          {facet.label}
        </div>
        <Edit3 className={`h-3 w-3 ${editing ? 'text-brand-500' : 'text-gray-300 group-hover:text-brand-400'}`} />
      </div>
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') {
              setDraft(facet.value);
              setEditing(false);
            }
          }}
          className="w-full bg-transparent text-sm font-medium text-gray-900 outline-none dark:text-white"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="block w-full text-left text-sm font-medium leading-snug text-gray-900 dark:text-white"
        >
          {facet.value}
        </button>
      )}
    </div>
  );
}

export default function IcpReviewCard({ icp, onFacetChange, onPreview, onBuildList }) {
  return (
    <div className="rounded-2xl border border-brand-200 bg-white p-5 shadow-xl shadow-brand-100/50 dark:border-brand-800/70 dark:bg-gray-900 dark:shadow-brand-950/20">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
            Here's what I understood
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <Edit3 className="h-3 w-3" />
            Click any box to tweak it
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold tracking-tight text-gray-950 dark:text-white">
        {icp.headline}
      </h2>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500 dark:text-gray-400">
        {icp.summary}
      </p>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        {icp.facets.map((facet) => (
          <FacetBox key={facet.key} facet={facet} onChange={onFacetChange} />
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-2 border-t border-gray-100 pt-4 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Check className="h-3.5 w-3.5 text-brand-500" />
          {icp.suggestedFilters.length} filters and {icp.suggestedAiColumns.length} AI columns prepared
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onBuildList}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-brand-300 hover:text-brand-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-brand-700 dark:hover:text-brand-300"
          >
            <Zap className="h-4 w-4" />
            Just build my list
          </button>
          <button
            type="button"
            onClick={onPreview}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
          >
            Preview prospect list
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
